<template>
    <section :class="['tk-case-notes-panel', { 'tk-detail-section': useCard }]">
        <div v-if="showHeader" class="tk-section-heading">
            <div>
                <span class="tk-section-heading__eyebrow">Contact history</span>
                <h2>Case Notes / Interactions</h2>
            </div>
        </div>

        <div v-if="showForm && canSaveNote && !optionsLoaded" class="tk-case-note-options-state">
            <template v-if="loadingOptions">
                <q-spinner color="primary" size="28px" />
                <span>Loading case note options...</span>
            </template>
            <q-banner v-else class="tk-alert-banner bg-red-1 text-red-10">
                Case note options could not be loaded.
                <template #action>
                    <q-btn flat no-caps color="red-10" label="Try Again" @click="loadOptions" />
                </template>
            </q-banner>
        </div>

        <q-form v-if="showForm && canSaveNote && optionsLoaded" ref="caseNoteForm" class="tk-case-note-form" @submit.prevent="saveNote">
            <div class="row q-col-gutter-md">
                <div class="col-12 col-md-4">
                    <p class="q-mb-xs"><strong>Contact Type</strong></p>
                    <q-select
                        v-model="form.contactTypeOptionId"
                        :options="contactTypeOptions"
                        outlined
                        dense
                        emit-value
                        map-options
                        lazy-rules="ondemand"
                        hide-bottom-space
                        :loading="loadingOptions"
                        :rules="[v => !!v || 'Contact type is required.']"
                    />
                </div>
                <div class="col-12 col-md-4">
                    <p class="q-mb-xs"><strong>Date</strong></p>
                    <q-input
                        v-model="form.contactAt"
                        outlined
                        dense
                        type="datetime-local"
                        lazy-rules="ondemand"
                        hide-bottom-space
                        :rules="[v => !!v || 'Date is required.']"
                    />
                </div>
                <div class="col-12 col-md-4">
                    <p class="q-mb-xs"><strong>Worker</strong></p>
                    <q-select
                        v-model="form.staffId"
                        :options="staffOptions"
                        outlined
                        dense
                        emit-value
                        map-options
                        lazy-rules="ondemand"
                        hide-bottom-space
                        :rules="[v => !!v || 'Worker is required.']"
                    />
                </div>
                <div class="col-12">
                    <p class="q-mb-xs"><strong>Additional Kaimahi</strong></p>
                    <q-select
                        v-model="form.additionalStaffIds"
                        :options="additionalStaffOptions"
                        outlined
                        dense
                        clearable
                        emit-value
                        map-options
                        multiple
                        use-chips
                        hide-bottom-space
                    />
                </div>
                <div v-if="canManageConfidentiality" class="col-12">
                    <section class="tk-confidentiality-editor">
                        <q-checkbox
                            v-model="form.isConfidential"
                            label="Confidential case note"
                            color="primary"
                        />
                        <p class="tk-confidentiality-editor__help q-mb-none">
                            When confidential, only you and the authorised kaimahi selected below can view this note.
                            Everyone else sees only its date and time.
                        </p>
                        <div v-if="form.isConfidential" class="q-mt-md">
                            <p class="q-mb-xs"><strong>Authorised Kaimahi</strong></p>
                            <q-select
                                v-model="form.authorizedViewerIds"
                                :options="authorizedViewerOptions"
                                outlined
                                dense
                                clearable
                                emit-value
                                map-options
                                multiple
                                use-chips
                                hide-bottom-space
                                hint="You are always authorised as the note's author."
                            />
                        </div>
                    </section>
                </div>
                <div class="col-12 col-md-4">
                    <p class="q-mb-xs"><strong>Time Worked</strong></p>
                    <q-select
                        v-model="form.timeWorkedMinutes"
                        :options="timeWorkedOptions"
                        :option-label="formatTimeWorkedOption"
                        outlined
                        dense
                        use-input
                        fill-input
                        hide-selected
                        input-debounce="0"
                        lazy-rules="ondemand"
                        hide-bottom-space
                        :rules="[v => isValidMinutes(v) || 'Enter whole minutes from 0 to 480.']"
                        @new-value="setTimeWorkedMinutes"
                    />
                    <span class="text-caption text-grey-7">Select a time block or type the exact minutes.</span>
                </div>
                <div class="col-12 col-md-4">
                    <p class="q-mb-xs"><strong>Contact Location</strong></p>
                    <q-select
                        v-model="form.contactLocationOptionId"
                        :options="contactLocationOptions"
                        outlined
                        dense
                        emit-value
                        map-options
                        lazy-rules="ondemand"
                        hide-bottom-space
                        :loading="loadingOptions"
                        :rules="[v => !!v || 'Contact location is required.']"
                    />
                </div>
                <div class="col-12">
                    <p class="q-mb-xs"><strong>Pou / Wellbeing Area</strong></p>
                    <TkOptionPillGroup
                        v-model="form.wellbeingIds"
                        :options="wellbeingOptions"
                    />
                </div>
                <div class="col-12">
                    <section class="tk-goal-editor">
                        <div class="tk-goal-editor__heading">
                            <div>
                                <h3>New Goals / Whāinga</h3>
                                <span>
                                    Add new goals for convenience. Existing goals are managed from the Goals section.
                                    New goals use the case note date as their start date.
                                </span>
                            </div>
                        </div>
                        <div class="tk-goal-editor__groups">
                            <section
                                v-for="area in selectedWellbeingAreas"
                                :key="area.id"
                                class="tk-goal-editor__group"
                            >
                                <header class="tk-goal-editor__group-heading">
                                    <h4>{{ area.title }}</h4>
                                    <q-btn
                                        flat
                                        dense
                                        no-caps
                                        type="button"
                                        color="primary"
                                        icon="add"
                                        label="Add Goal"
                                        :aria-label="`Add goal for ${area.title}`"
                                        @click="addGoal(area.id)"
                                    />
                                </header>
                                <div
                                    v-for="goal in goalsForWellbeing(area.id)"
                                    :key="goal.draftId"
                                    class="tk-goal-editor__row"
                                >
                                    <q-input
                                        v-model="goal.description"
                                        class="col"
                                        outlined
                                        dense
                                        type="textarea"
                                        rows="2"
                                        label="Description"
                                        lazy-rules="ondemand"
                                        hide-bottom-space
                                        :rules="[v => !!String(v ?? '').trim() || 'Description is required.']"
                                    />
                                    <q-btn
                                        flat
                                        round
                                        dense
                                        type="button"
                                        color="negative"
                                        icon="delete_outline"
                                        :aria-label="`Remove goal from ${area.title}`"
                                        @click="removeGoal(goal.draftId)"
                                    />
                                </div>
                            </section>
                        </div>
                    </section>
                </div>
                <div class="col-12">
                    <p class="q-mb-xs"><strong>With Whom</strong></p>
                    <q-field
                        :model-value="form.withWhomOptionIds"
                        class="tk-case-note-option-field"
                        borderless
                        dense
                        lazy-rules="ondemand"
                        hide-bottom-space
                        :rules="[v => !!v?.length || 'With whom is required.']"
                    >
                        <template #control>
                            <TkOptionPillGroup
                                v-model="form.withWhomOptionIds"
                                :options="withWhomOptions"
                            />
                        </template>
                    </q-field>
                </div>
                <div class="col-12">
                    <section class="tk-risk-editor">
                        <div class="tk-risk-editor__heading">
                            <div>
                                <h3>Risk Assessment</h3>
                                <span>Select all that apply.</span>
                            </div>
                        </div>
                        <TkOptionPillGroup
                            v-model="form.riskTypeOptionIds"
                            :options="riskTypeOptions"
                        />

                        <p v-if="hasNoRisksIdentified" class="tk-risk-editor__no-risks">
                            No risks identified
                        </p>

                        <div v-if="form.riskDetails.length" class="tk-risk-editor__cards">
                            <article
                                v-for="(risk, index) in form.riskDetails"
                                :key="risk.riskTypeOptionId"
                                class="tk-risk-editor__card"
                            >
                                <header class="tk-risk-editor__card-heading">
                                    <div>
                                        <span>Risk {{ index + 1 }}</span>
                                        <h4>{{ riskDetailLabel(risk) }}</h4>
                                    </div>
                                    <q-btn
                                        flat
                                        round
                                        dense
                                        type="button"
                                        icon="o_close"
                                        color="grey-7"
                                        :aria-label="`Remove ${riskDetailLabel(risk)}`"
                                        @click="removeRisk(risk.riskTypeOptionId)"
                                    />
                                </header>
                                <div class="row q-col-gutter-md">
                                    <div class="col-12 col-md-7">
                                        <p class="q-mb-xs"><strong>Description</strong></p>
                                        <q-input
                                            v-model="risk.description"
                                            outlined
                                            dense
                                            type="textarea"
                                            rows="2"
                                            lazy-rules="ondemand"
                                            hide-bottom-space
                                            :rules="[v => !!String(v ?? '').trim() || 'Description is required.']"
                                        />
                                    </div>
                                    <div class="col-12 col-md-5">
                                        <p class="q-mb-xs"><strong>Managed</strong></p>
                                        <q-field
                                            :model-value="risk.isManaged"
                                            borderless
                                            dense
                                            hide-bottom-space
                                            :rules="[v => v === true || v === false || 'Select Yes or No.']"
                                        >
                                            <template #control>
                                                <q-btn-toggle
                                                    v-model="risk.isManaged"
                                                    no-caps
                                                    unelevated
                                                    spread
                                                    toggle-color="primary"
                                                    color="white"
                                                    text-color="primary"
                                                    :options="riskManagedOptions"
                                                    @update:model-value="onRiskManagedChange(risk)"
                                                />
                                            </template>
                                        </q-field>
                                    </div>
                                    <div v-if="risk.isManaged === false" class="col-12">
                                        <p class="q-mb-xs"><strong>How will this risk be managed?</strong></p>
                                        <q-input
                                            v-model="risk.managementPlan"
                                            outlined
                                            dense
                                            type="textarea"
                                            rows="2"
                                            lazy-rules="ondemand"
                                            hide-bottom-space
                                            :rules="[
                                                v => !!String(v ?? '').trim() || 'Risk management details are required.',
                                            ]"
                                        />
                                    </div>
                                </div>
                            </article>
                        </div>
                    </section>
                </div>
                <div class="col-12">
                    <q-checkbox
                        v-model="form.faceToFaceEngagement"
                        label="Face to Face engagement"
                        color="primary"
                    />
                    <div>
                        <q-checkbox v-model="form.noShow" label="No show" color="primary" />
                    </div>
                    <div>
                        <q-checkbox v-model="form.cancelledByPrison" label="Cancelled by prison" color="primary" />
                    </div>
                </div>
                <div class="col-12">
                    <div class="tk-case-note-label-row q-mb-xs">
                        <p class="q-mb-none"><strong>Notes</strong></p>
                        <q-btn
                            flat
                            dense
                            no-caps
                            type="button"
                            color="primary"
                            label="Add case note prompt"
                            @click="addCaseNotePrompt"
                        />
                    </div>
                    <q-input
                        v-model="form.body"
                        class="tk-case-note-notes"
                        outlined
                        dense
                        type="textarea"
                        rows="8"
                        lazy-rules="ondemand"
                        hide-bottom-space
                        :rules="[v => !!String(v ?? '').trim() || 'Notes are required.']"
                    />
                </div>
                <div class="col-12">
                    <p class="q-mb-xs"><strong>Attachments</strong></p>
                    <TkDocumentsPanel
                        v-if="isEditing"
                        owner-type="engagement"
                        :owner-id="engagementId"
                        :case-note-id="noteToEdit.id"
                    />
                    <template v-else>
                        <div class="tk-case-note-attachment-upload">
                            <q-file
                                v-model="pendingAttachmentFile"
                                outlined
                                dense
                                clearable
                                label="Choose document"
                                max-file-size="26214400"
                                @rejected="onAttachmentRejected"
                            >
                                <template v-slot:prepend>
                                    <q-icon name="o_attach_file" />
                                </template>
                            </q-file>
                            <q-input
                                v-model="pendingAttachmentDescription"
                                outlined
                                dense
                                autogrow
                                maxlength="1000"
                                label="Description"
                            />
                            <q-btn
                                class="tk-case-note-attachment-upload__action"
                                unelevated
                                no-caps
                                type="button"
                                color="primary"
                                label="Add Attachment"
                                :disable="!pendingAttachmentFile"
                                @click="addPendingAttachment"
                            />
                        </div>
                        <q-list v-if="pendingAttachments.length" separator class="tk-case-note-attachment-list">
                            <q-item v-for="(attachment, index) in pendingAttachments" :key="index">
                                <q-item-section avatar>
                                    <q-icon name="o_description" color="primary" size="24px" />
                                </q-item-section>
                                <q-item-section>
                                    <q-item-label>{{ attachment.file.name }}</q-item-label>
                                    <q-item-label v-if="attachment.description" caption>{{ attachment.description }}</q-item-label>
                                </q-item-section>
                                <q-item-section side>
                                    <q-btn flat round dense color="negative" icon="o_delete" type="button" @click="removePendingAttachment(index)">
                                        <q-tooltip class="text-body2">Remove</q-tooltip>
                                    </q-btn>
                                </q-item-section>
                            </q-item>
                        </q-list>
                        <span v-else class="text-caption text-grey-7">Attachments upload once the note is saved.</span>
                    </template>
                </div>
            </div>
            <div class="tk-case-note-actions">
                <q-btn
                    unelevated
                    no-caps
                    color="primary"
                    :icon="isEditing ? 'o_save' : 'o_note_add'"
                    :label="isEditing ? 'Save Changes' : 'Save Case Note'"
                    type="submit"
                    :loading="saving"
                    :disable="loadingOptions"
                />
            </div>
        </q-form>

        <q-separator v-if="showForm && showList" class="q-my-md" />

        <template v-if="showList">
            <q-inner-loading :showing="loading" />
            <div v-if="!loading && !displayedNotes.length" class="tk-case-note-empty">
                <q-icon name="o_speaker_notes_off" size="24px" />
                <span>No case notes or interactions have been added.</span>
            </div>
            <div v-else class="tk-case-note-cards">
                <TkCaseNoteCard
                    v-for="note in displayedNotes"
                    :key="note.id"
                    :note="note"
                    @edit="requestEdit"
                />
            </div>
        </template>
    </section>
</template>

<script>
import api from "@/services/api/api";
import notify from "@/services/util/notify";
import token from "@/services/util/roleHandler";
import Roles from "@/services/util/roles";
import TkCaseNoteCard from "@/components/TkClients/TkCaseNoteCard.vue";
import TkDocumentsPanel from "@/components/TkClients/TkDocumentsPanel.vue";
import TkOptionPillGroup from "@/components/TkClients/TkOptionPillGroup.vue";
import {
    dateOnlyFromDateTimeLocal,
    fromDateTimeLocal,
    toDateTimeLocal,
} from "@/services/util/tkClients";

const buildForm = (currentStaffId, assignedWorkerId) => ({
    contactAt: toDateTimeLocal(new Date()),
    staffId: currentStaffId || assignedWorkerId || null,
    additionalStaffIds: [],
    wellbeingIds: [],
    contactTypeOptionId: null,
    timeWorkedMinutes: "",
    withWhomOptionIds: [],
    contactLocationOptionId: null,
    riskTypeOptionIds: [],
    riskDetails: [],
    newGoals: [],
    faceToFaceEngagement: false,
    noShow: false,
    cancelledByPrison: false,
    body: "",
    contactTypeText: null,
    contactMethod: null,
    location: null,
    contactAttendanceOptionId: null,
    isConfidential: false,
    authorizedViewerIds: [],
});

const buildFormFromNote = (note, currentStaffId, assignedWorkerId) => {
    if (!note) return buildForm(currentStaffId, assignedWorkerId);
    return {
        contactAt: toDateTimeLocal(note.contact_at),
        staffId: note.staff_id || currentStaffId || assignedWorkerId || null,
        additionalStaffIds: (note.additional_staff || []).map(staff => staff.staff_id),
        contactTypeOptionId: note.contact_type_option_id || null,
        timeWorkedMinutes: note.time_worked_minutes,
        withWhomOptionIds: (note.with_whom_options || []).map(option => String(option.id)),
        contactLocationOptionId: note.contact_location_option_id || null,
        riskTypeOptionIds: (note.risk_assessments || []).map(risk => String(risk.id)),
        riskDetails: (note.risk_assessments || [])
            .filter(risk => risk.key !== "no_risks_identified")
            .map(risk => ({
                riskTypeOptionId: String(risk.id),
                label: risk.label,
                description: risk.description || "",
                isManaged: risk.is_managed == null ? null : !!risk.is_managed,
                managementPlan: risk.management_plan || "",
            })),
        newGoals: [],
        faceToFaceEngagement: !!note.face_to_face_engagement,
        noShow: !!note.no_show,
        cancelledByPrison: !!note.cancelled_by_prison,
        body: note.body || "",
        wellbeingIds: note.wellbeing_areas?.length
            ? note.wellbeing_areas.map(area => area.id)
            : note.wellbeing_id ? [note.wellbeing_id] : [],
        contactTypeText: note.contact_type_text || null,
        contactMethod: note.contact_method || null,
        location: note.location || null,
        contactAttendanceOptionId: note.contact_attendance_option_id || null,
        isConfidential: !!note.is_confidential,
        authorizedViewerIds: (note.authorized_viewers || []).map(viewer => viewer.staff_id),
    };
};

const toOption = (option) => ({
    label: option.label,
    value: option.id,
});

const toStringValueOption = (option) => ({
    label: option.label,
    value: String(option.id),
});

const toWellbeingOption = (area) => ({
    label: area.title,
    value: area.id,
});

const noteTimestamp = (note) => {
    const timestamp = new Date(note.contact_at).getTime();
    return Number.isFinite(timestamp) ? timestamp : 0;
};

const TIME_WORKED_OPTIONS = [5, 15, 30, 60, 120, 180, 240, 300, 360, 420, 480];
const RISK_MANAGED_OPTIONS = [
    { label: "Yes", value: true },
    { label: "No", value: false },
];

const CASE_NOTE_PROMPT = [
    "Date / Location of hui:",
    "Attendees (other than whaiora):",
    "Ukaipo / Relationships (Whānau & Community supports):",
    "Taiao / Environment (Accommodation):",
    "Rawa / Personal Resources (Training/Employment/Skills):",
    "Tinana / Physical wellbeing:",
    "Hinengāro / Mental wellbeing:",
    "Wairua / Spiritual wellbeing:",
    "Next Steps/Actions:",
].join("\n\n");

export default {
    name: "TkCaseNotesPanel",
    components: {
        TkCaseNoteCard,
        TkDocumentsPanel,
        TkOptionPillGroup,
    },
    emits: ["count-changed", "created", "updated", "edit", "goals-created"],
    props: {
        canCreate: { type: Boolean, default: false },
        canEdit: { type: Boolean, default: false },
        personId: {
            type: [Number, String],
            required: true,
        },
        engagementId: {
            type: [Number, String],
            required: true,
        },
        staffOptions: {
            type: Array,
            required: true,
        },
        currentStaffId: {
            type: Number,
            default: null,
        },
        assignedWorkerId: {
            type: [Number, String, null],
            default: null,
        },
        showHeader: {
            type: Boolean,
            default: true,
        },
        showForm: {
            type: Boolean,
            default: true,
        },
        showList: {
            type: Boolean,
            default: true,
        },
        useCard: {
            type: Boolean,
            default: true,
        },
        noteToEdit: {
            type: Object,
            default: null,
        },
        noteLimit: {
            type: Number,
            default: null,
        },
    },
    data() {
        return {
            loading: false,
            loadingOptions: this.showForm && !this.$store.state.caseNoteOptions.loaded,
            saving: false,
            notes: [],
            form: buildForm(this.currentStaffId, this.assignedWorkerId),
            pendingAttachmentFile: null,
            pendingAttachmentDescription: "",
            pendingAttachments: [],
            nextGoalDraftId: 1,
        };
    },
    computed: {
        canSaveNote() {
            return this.isEditing ? this.canEdit : this.canCreate;
        },
        lookupOptions() {
            return this.$store.state.caseNoteOptions.lookupOptions;
        },
        wellbeingAreas() {
            return this.$store.state.caseNoteOptions.wellbeingAreas;
        },
        optionsLoaded() {
            return this.$store.state.caseNoteOptions.loaded;
        },
        contactTypeOptions() {
            return this.lookupOptions.contact_type.map(toOption);
        },
        withWhomOptions() {
            return this.lookupOptions.with_whom.map(toStringValueOption);
        },
        contactLocationOptions() {
            return this.lookupOptions.contact_location.map(toOption);
        },
        riskTypeOptions() {
            return this.lookupOptions.risk_type.map(toStringValueOption);
        },
        noRisksOptionId() {
            const option = this.lookupOptions.risk_type
                .find(riskType => riskType.key === "no_risks_identified");
            if (option) return String(option.id);
            const selectedOption = (this.noteToEdit?.risk_assessments || [])
                .find(riskType => riskType.key === "no_risks_identified");
            return selectedOption ? String(selectedOption.id) : null;
        },
        hasNoRisksIdentified() {
            return (this.form.riskTypeOptionIds || [])
                .some(optionId => String(optionId) === this.noRisksOptionId);
        },
        riskManagedOptions() {
            return RISK_MANAGED_OPTIONS;
        },
        timeWorkedOptions() {
            return TIME_WORKED_OPTIONS;
        },
        wellbeingOptions() {
            return this.wellbeingAreas.map(toWellbeingOption);
        },
        selectedWellbeingAreas() {
            const selectedIds = new Set((this.form.wellbeingIds || []).map(String));
            return this.wellbeingAreas.filter(area => selectedIds.has(String(area.id)));
        },
        additionalStaffOptions() {
            const primaryStaffId = String(this.form.staffId ?? "");
            return this.staffOptions.filter(option => String(option.value) !== primaryStaffId);
        },
        authorizedViewerOptions() {
            const authorStaffId = String(this.noteToEdit?.created_by || this.currentStaffId || "");
            return this.staffOptions.filter(option => String(option.value) !== authorStaffId);
        },
        canManageConfidentiality() {
            const role = token.getRole();
            const isTeamLeaderOrAbove = [Roles.TeamLeader, Roles.Admin, Roles.Developer]
                .includes(role);
            if (!isTeamLeaderOrAbove) return false;
            if (!this.isEditing) return true;
            return String(this.noteToEdit?.created_by || "") === String(this.currentStaffId || "");
        },
        isEditing() {
            return !!this.noteToEdit?.id;
        },
        displayedNotes() {
            const sortedNotes = [...this.notes].sort((first, second) => noteTimestamp(second) - noteTimestamp(first));
            return this.noteLimit === null ? sortedNotes : sortedNotes.slice(0, this.noteLimit);
        },
    },
    watch: {
        engagementId() {
            if (this.showList) this.loadNotes();
        },
        currentStaffId(value) {
            if (!this.form.staffId) this.form.staffId = value || this.assignedWorkerId || null;
        },
        assignedWorkerId(value) {
            if (!this.form.staffId) this.form.staffId = this.currentStaffId || value || null;
        },
        "form.staffId"(value) {
            this.form.additionalStaffIds = (this.form.additionalStaffIds || [])
                .filter(staffId => String(staffId) !== String(value));
        },
        "form.isConfidential"(value) {
            if (!value) this.form.authorizedViewerIds = [];
        },
        "form.wellbeingIds"(value) {
            const selectedIds = new Set((value || []).map(String));
            this.form.newGoals = (this.form.newGoals || [])
                .filter(goal => selectedIds.has(String(goal.wellbeingId)));
        },
        "form.riskTypeOptionIds"(value, previousValue) {
            const noRisksOptionId = this.noRisksOptionId;
            const selectedIds = (value || []).map(String);
            const previousIds = (previousValue || []).map(String);

            if (noRisksOptionId && selectedIds.includes(noRisksOptionId) && selectedIds.length > 1) {
                const noRisksWasAdded = !previousIds.includes(noRisksOptionId);
                this.form.riskTypeOptionIds = noRisksWasAdded
                    ? [noRisksOptionId]
                    : selectedIds.filter(optionId => optionId !== noRisksOptionId);
                return;
            }
            this.syncRiskDetails(selectedIds);
        },
        noteToEdit: {
            immediate: true,
            handler(note) {
                this.form = buildFormFromNote(note, this.currentStaffId, this.assignedWorkerId);
                this.pendingAttachmentFile = null;
                this.pendingAttachmentDescription = "";
                this.pendingAttachments = [];
            },
        },
    },
    async mounted() {
        const tasks = [];
        if (this.showForm) tasks.push(this.loadOptions());
        if (this.showList) tasks.push(this.loadNotes());
        await Promise.all(tasks);
    },
    methods: {
        formatTimeWorkedOption(value) {
            const minutes = Number(value);
            if (!Number.isFinite(minutes) || minutes < 60 || minutes % 60 !== 0) {
                return `${value} min`;
            }
            const hours = minutes / 60;
            return `${hours} ${hours === 1 ? "hr" : "hrs"}`;
        },
        setTimeWorkedMinutes(value, done) {
            const minutes = Number(value);
            done(Number.isFinite(minutes) ? minutes : value, "add");
        },
        addCaseNotePrompt() {
            const existingBody = String(this.form.body || "").trimEnd();
            this.form.body = existingBody
                ? `${existingBody}\n\n${CASE_NOTE_PROMPT}`
                : CASE_NOTE_PROMPT;
        },
        goalsForWellbeing(wellbeingId) {
            return (this.form.newGoals || [])
                .filter(goal => String(goal.wellbeingId) === String(wellbeingId));
        },
        addGoal(wellbeingId) {
            const selectedIds = new Set((this.form.wellbeingIds || []).map(String));
            if (!selectedIds.has(String(wellbeingId))) return;
            this.form.newGoals.push({
                draftId: this.nextGoalDraftId,
                wellbeingId,
                description: "",
            });
            this.nextGoalDraftId += 1;
        },
        removeGoal(draftId) {
            this.form.newGoals = (this.form.newGoals || [])
                .filter(goal => goal.draftId !== draftId);
        },
        riskDetailLabel(risk) {
            const option = this.lookupOptions.risk_type
                .find(riskType => String(riskType.id) === String(risk.riskTypeOptionId));
            return option?.label || risk.label || "Risk";
        },
        syncRiskDetails(selectedIds) {
            const existingDetails = new Map(
                (this.form.riskDetails || [])
                    .map(risk => [String(risk.riskTypeOptionId), risk])
            );
            this.form.riskDetails = selectedIds
                .filter(optionId => optionId !== this.noRisksOptionId)
                .map(optionId => existingDetails.get(optionId) || {
                    riskTypeOptionId: optionId,
                    label: this.riskTypeOptions.find(option => option.value === optionId)?.label || "Risk",
                    description: "",
                    isManaged: null,
                    managementPlan: "",
                });
        },
        removeRisk(riskTypeOptionId) {
            this.form.riskTypeOptionIds = (this.form.riskTypeOptionIds || [])
                .filter(optionId => String(optionId) !== String(riskTypeOptionId));
        },
        onRiskManagedChange(risk) {
            if (risk.isManaged !== false) risk.managementPlan = "";
        },
        requestEdit(note) {
            if (note.can_edit === false) return;
            this.$emit("edit", note);
        },
        isValidMinutes(value) {
            const minutes = Number(value);
            return Number.isInteger(minutes) && minutes >= 0 && minutes <= 480;
        },
        onAttachmentRejected() {
            notify.error("Document must be 25 MB or smaller", "top");
        },
        addPendingAttachment() {
            if (!this.pendingAttachmentFile) return;
            this.pendingAttachments.push({
                file: this.pendingAttachmentFile,
                description: this.pendingAttachmentDescription.trim(),
            });
            this.pendingAttachmentFile = null;
            this.pendingAttachmentDescription = "";
        },
        removePendingAttachment(index) {
            this.pendingAttachments.splice(index, 1);
        },
        async uploadPendingAttachments(caseNoteId) {
            const attachments = this.pendingAttachments;
            this.pendingAttachments = [];
            for (const attachment of attachments) {
                try {
                    await api.documents.uploadDocument(
                        "engagement",
                        this.engagementId,
                        attachment.file,
                        attachment.description,
                        caseNoteId
                    );
                } catch (error) {
                    notify.error(`Failed to upload ${attachment.file.name}`, "top");
                }
            }
        },
        async loadOptions() {
            if (this.optionsLoaded) {
                this.loadingOptions = false;
                return;
            }

            this.loadingOptions = true;
            try {
                await this.$store.dispatch("caseNoteOptions/load");
            } catch (error) {
                notify.error("Failed to load case note options", "top");
            } finally {
                this.loadingOptions = false;
            }
        },
        async loadNotes() {
            this.loading = true;
            try {
                const res = await api.tkPeople.listCaseNotes(this.personId, this.engagementId);
                this.notes = res.data || [];
                this.$emit("count-changed", this.notes.length);
            } catch (error) {
                notify.error("Failed to load case notes", "top");
            } finally {
                this.loading = false;
            }
        },
        async saveNote() {
            if (!this.canSaveNote) return;
            const isValid = await this.$refs.caseNoteForm?.validate();
            if (!isValid) return;

            const contactAt = fromDateTimeLocal(this.form.contactAt);
            if (!contactAt || !String(this.form.body || "").trim()) return;
            if (!this.form.contactTypeOptionId || !this.form.withWhomOptionIds?.length || !this.form.contactLocationOptionId) return;
            if (!this.isValidMinutes(this.form.timeWorkedMinutes)) return;

            const isEditing = this.isEditing;
            this.saving = true;
            try {
                const payload = {
                    staff_id: this.form.staffId,
                    additional_staff_ids: this.form.additionalStaffIds || [],
                    contact_at: contactAt,
                    case_note_date: dateOnlyFromDateTimeLocal(this.form.contactAt),
                    wellbeing_ids: this.form.wellbeingIds || [],
                    contact_type_option_id: this.form.contactTypeOptionId,
                    contact_type: this.form.contactTypeText,
                    contact_method: this.form.contactMethod,
                    with_whom_option_ids: this.form.withWhomOptionIds,
                    contact_location_option_id: this.form.contactLocationOptionId,
                    location: this.form.location,
                    risk_assessments: (this.form.riskTypeOptionIds || []).map(optionId => {
                        const normalizedOptionId = String(optionId);
                        const risk = (this.form.riskDetails || [])
                            .find(detail => String(detail.riskTypeOptionId) === normalizedOptionId);
                        return {
                            risk_type_option_id: normalizedOptionId,
                            description: risk ? String(risk.description || "").trim() || null : null,
                            is_managed: risk?.isManaged ?? null,
                            management_plan: risk?.isManaged === false
                                ? String(risk.managementPlan || "").trim() || null
                                : null,
                        };
                    }),
                    face_to_face_engagement: this.form.faceToFaceEngagement,
                    no_show: this.form.noShow,
                    cancelled_by_prison: this.form.cancelledByPrison,
                    time_worked_minutes: this.form.timeWorkedMinutes,
                    contact_attendance_option_id: this.form.contactAttendanceOptionId,
                    body: this.form.body.trim(),
                    new_goals: (this.form.newGoals || []).map(goal => ({
                        wellbeing_id: goal.wellbeingId,
                        description: String(goal.description || "").trim(),
                    })),
                    is_confidential: this.form.isConfidential,
                    authorized_viewer_ids: this.form.isConfidential
                        ? this.form.authorizedViewerIds || []
                        : [],
                };
                const res = isEditing
                    ? await api.tkPeople.updateCaseNote(
                        this.personId,
                        this.engagementId,
                        this.noteToEdit.id,
                        payload,
                    )
                    : await api.tkPeople.createCaseNote(this.personId, this.engagementId, payload);
                if (res.data) {
                    this.notes = [res.data, ...this.notes.filter(note => note.id !== res.data.id)];
                }
                if (!isEditing && this.pendingAttachments.length && res.data?.id) {
                    await this.uploadPendingAttachments(res.data.id);
                }
                const createdGoalCount = payload.new_goals.length;
                this.form = buildForm(this.currentStaffId, this.assignedWorkerId);
                if (createdGoalCount) this.$emit("goals-created", createdGoalCount);
                this.$emit(isEditing ? "updated" : "created", res.data);
                const caseNoteMessage = isEditing ? "Case note updated" : "Case note added";
                notify.primary(
                    createdGoalCount
                        ? `${caseNoteMessage} and ${createdGoalCount} ${createdGoalCount === 1 ? "goal" : "goals"} created`
                        : caseNoteMessage,
                    "top"
                );
            } catch (error) {
                const message = error?.response?.statusText;
                const fallback = isEditing ? "Failed to update case note" : "Failed to add case note";
                notify.error(message && message !== "Internal Server Error" ? message : fallback, "top");
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style scoped>
.tk-case-notes-panel {
    min-width: 0;
}

.tk-detail-section {
    padding: 20px;
    border: 1px solid rgba(47, 62, 70, 0.08);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.96);
    box-shadow: 0 10px 28px rgba(47, 62, 70, 0.06);
}

.tk-section-heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 16px;
}

.tk-section-heading__eyebrow {
    display: block;
    margin-bottom: 4px;
    color: #78837d;
    font-size: 0.74rem;
    font-weight: 550;
    letter-spacing: 0.08em;
    line-height: 1.2;
    text-transform: uppercase;
}

.tk-section-heading h2 {
    margin: 0;
    color: #3d4b46;
    font-size: 1.12rem;
    font-weight: 600;
    letter-spacing: 0.035em;
    line-height: 1.25;
}

.tk-case-note-form {
    padding: 0;
}

.tk-confidentiality-editor {
    padding: 12px 14px;
    border: 1px solid rgba(47, 62, 70, 0.12);
    border-radius: 9px;
    background: #f8faf8;
}

.tk-confidentiality-editor__help {
    padding-left: 40px;
    color: #6f7c75;
    font-size: 0.8rem;
}

.tk-case-note-options-state {
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 80px;
    color: #5d6b70;
}

.tk-case-note-options-state .q-banner {
    width: 100%;
}

.tk-case-note-form p {
    color: #68756e;
    font-size: 0.84rem;
    margin-bottom: 7px;
}

.tk-case-note-form p strong {
    font-weight: 550;
    letter-spacing: 0.02em;
}

.tk-case-note-form p strong::after {
    content: ":";
}

.tk-goal-editor {
    padding: 12px;
    border: 1px solid rgba(47, 62, 70, 0.1);
    border-radius: 9px;
    background: #f8faf8;
}

.tk-goal-editor__heading {
    margin-bottom: 10px;
}

.tk-goal-editor__heading h3,
.tk-goal-editor__group-heading h4 {
    margin: 0;
    color: #3d4b46;
}

.tk-goal-editor__heading h3 {
    font-size: 0.95rem;
    font-weight: 600;
    letter-spacing: 0.025em;
    line-height: 1.3;
}

.tk-goal-editor__heading span {
    display: block;
    margin-top: 2px;
    color: #748078;
    font-size: 0.76rem;
    line-height: 1.4;
}

.tk-goal-editor__groups {
    display: grid;
    gap: 8px;
}

.tk-goal-editor__group {
    padding: 8px 10px;
    border: 1px solid rgba(77, 143, 119, 0.16);
    border-radius: 8px;
    background: white;
}

.tk-goal-editor__group-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.tk-goal-editor__group-heading h4 {
    font-size: 0.86rem;
    font-weight: 550;
    line-height: 1.35;
}

.tk-goal-editor__row {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    margin-top: 8px;
}

.tk-risk-editor {
    padding: 12px;
    border: 1px solid rgba(47, 62, 70, 0.1);
    border-radius: 9px;
    background: #f8faf8;
}

.tk-risk-editor__heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 8px;
}

.tk-risk-editor__heading h3,
.tk-risk-editor__card-heading h4 {
    margin: 0;
    color: #3d4b46;
}

.tk-risk-editor__heading h3 {
    font-size: 0.95rem;
    font-weight: 600;
    letter-spacing: 0.025em;
    line-height: 1.3;
}

.tk-risk-editor__heading span {
    display: block;
    margin-top: 1px;
    color: #748078;
    font-size: 0.76rem;
    line-height: 1.35;
}

.tk-risk-editor__no-risks {
    margin: 8px 0 0;
    color: #46544e;
}

.tk-risk-editor__cards {
    display: grid;
    gap: 8px;
    margin-top: 8px;
}

.tk-risk-editor__card {
    padding: 10px 12px;
    border: 1px solid rgba(77, 143, 119, 0.2);
    border-left: 4px solid #4d8f77;
    border-radius: 8px;
    background: white;
}

.tk-risk-editor__card-heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 8px;
}

.tk-risk-editor__card-heading > div {
    display: flex;
    align-items: baseline;
    gap: 7px;
}

.tk-risk-editor__card-heading span {
    color: #4d8f77;
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.tk-risk-editor__card-heading h4 {
    margin-top: 0;
    font-size: 0.9rem;
    font-weight: 550;
    line-height: 1.35;
}

.tk-risk-editor__card :deep(.q-btn-toggle) {
    width: 100%;
    border: 1px solid rgba(77, 143, 119, 0.28);
}

.tk-case-note-label-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.tk-case-note-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
}

.tk-case-note-empty {
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 54px;
    padding: 12px;
    border: 1px solid rgba(47, 62, 70, 0.08);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.97);
    color: #5d6b70;
    box-shadow: 0 8px 20px rgba(47, 62, 70, 0.05);
}

.tk-case-note-cards {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.tk-case-note-option-field {
    padding: 0;
}

.tk-case-note-option-field :deep(.q-field__control),
.tk-case-note-option-field :deep(.q-field__native) {
    min-height: 34px;
    padding: 0;
}

.tk-case-note-form :deep(.q-field__native),
.tk-case-note-form :deep(.q-field__input),
.tk-case-note-form :deep(.q-field__marginal) {
    color: #46544e;
    font-weight: 400;
}

.tk-case-note-form :deep(.q-field__native::placeholder),
.tk-case-note-form :deep(.q-field__input::placeholder) {
    color: #929c96;
}

.tk-case-note-attachment-upload {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    align-items: start;
    max-width: 100%;
}

.tk-case-note-attachment-upload > * {
    min-width: 0;
}

.tk-case-note-attachment-upload__action {
    grid-column: 1 / -1;
    justify-self: end;
}

.tk-case-note-attachment-list {
    margin-top: 10px;
    border: 1px solid rgba(47, 62, 70, 0.07);
    border-radius: 8px;
    overflow: hidden;
}

@media (max-width: 700px) {
    .tk-case-note-actions {
        justify-content: stretch;
    }

    .tk-case-note-actions :deep(.q-btn) {
        width: 100%;
    }

    .tk-case-note-attachment-upload {
        grid-template-columns: 1fr;
    }

    .tk-case-note-attachment-upload__action {
        width: 100%;
    }

}
</style>
